/**
 * 音效工具類
 * 使用 Web Audio API 生成各種警示音效
 */

class SoundEffects {
  constructor() {
    this.audioContext = null;
  }

  /**
   * 初始化 AudioContext（需要在用戶互動後調用）
   */
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  /**
   * 播放錯誤警示音（刺耳的嗶嗶聲）
   * @param {number} duration - 持續時間（毫秒）
   */
  playError(duration = 200) {
    const ctx = this.init();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // 使用方波（square wave）產生刺耳的音效
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(800, ctx.currentTime); // 高頻率 800Hz

    // 音量控制（避免太大聲）
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration / 1000);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration / 1000);
  }

  /**
   * 播放雙音錯誤警示（更刺耳）
   */
  playDoubleBeepError() {
    this.playError(150);
    setTimeout(() => this.playError(150), 200);
  }

  /**
   * 播放三音錯誤警示（類似警報）
   */
  playAlarmError() {
    const ctx = this.init();
    const times = [0, 150, 300];

    times.forEach((delay) => {
      setTimeout(() => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.type = 'sawtooth'; // 鋸齒波更刺耳
        oscillator.frequency.setValueAtTime(1000, ctx.currentTime);

        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);

        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.1);
      }, delay);
    });
  }

  /**
   * 播放低沈吵雜的錯誤音（類似蜂鳴器）
   */
  playBuzzerError() {
    const ctx = this.init();

    // 創建主要低頻音
    const oscillator1 = ctx.createOscillator();
    const oscillator2 = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator1.connect(gainNode);
    oscillator2.connect(gainNode);
    gainNode.connect(ctx.destination);

    // 使用低頻的方波和鋸齒波疊加，產生吵雜感
    oscillator1.type = 'square';
    oscillator1.frequency.setValueAtTime(180, ctx.currentTime); // 低沈的 180Hz

    oscillator2.type = 'sawtooth';
    oscillator2.frequency.setValueAtTime(185, ctx.currentTime); // 略微不和諧產生嗡嗡聲

    // 音量較大，持續較長
    gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

    oscillator1.start(ctx.currentTime);
    oscillator1.stop(ctx.currentTime + 0.5);
    oscillator2.start(ctx.currentTime);
    oscillator2.stop(ctx.currentTime + 0.5);
  }

  /**
   * 播放成功音效（柔和的提示音）
   */
  playSuccess() {
    const ctx = this.init();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'sine'; // 正弦波比較柔和
    oscillator.frequency.setValueAtTime(600, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.1);

    gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.15);
  }

  /**
   * 播放警告音效（中等刺耳）
   */
  playWarning() {
    const ctx = this.init();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(400, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.2);

    gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.2);
  }

  /**
   * 播放掃描音效（快速的嗶聲）
   */
  playScan() {
    const ctx = this.init();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(1200, ctx.currentTime);

    gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.05);
  }
}

// 建立單例
const soundEffects = new SoundEffects();

export default soundEffects;
